/* import __COLOCATED_TEMPLATE__ from './import-glossary-modal.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { Promise as EmberPromise } from 'rsvp';
import csvParser from 'papaparse';
import { type TaskGenerator } from 'ember-concurrency';
import { type ParseResult } from 'papaparse';
import { taskFor } from 'ember-concurrency-ts';
import type GlossaryService from 'embercom/services/glossary-service';
import { isLocaleSupported } from 'embercom/components/content-service/ai-content-library/helpers';
import type Store from '@ember-data/store';

interface Args {
  isOpen: boolean;
  onModalClose: () => void;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class ImportGlossaryModal extends Component<Signature> {
  @service declare intl: any;
  @service declare notificationsService: any;
  @service declare session: any;
  @service declare intercomEventService: any;
  @service declare glossaryService: GlossaryService;
  @service declare store: Store;
  @service declare appService: AppService;

  @tracked file: File | null = null;
  @tracked fileIsValid = false;
  @tracked fileFailedToUpload = false;
  @tracked parsingErrorMessage = '';
  @tracked csvData: string[][] = [];

  async _parseCsvFile(file: File): Promise<ParseResult<any>> {
    return new EmberPromise((resolve, reject) => {
      let complete = (results: any) => {
        if (results.errors.length) {
          if (results.errors.some((error: any) => error.code !== 'UndetectableDelimiter')) {
            reject(results);
          }
        }
        resolve(results);
      };
      csvParser.parse(file, { complete, error: reject });
    });
  }

  async isValidCsvStructure(data: any[][]) {
    if (!Array.isArray(data) || data.length < 2) {
      this.parsingErrorMessage = this.intl.t(
        'new-settings.workspace.multilingual.glossary.import.no-entries-error',
      );
      return false;
    }

    let firstRowLength = data[0].length;
    if (firstRowLength === 0) {
      this.parsingErrorMessage = this.intl.t(
        'new-settings.workspace.multilingual.glossary.import.no-locale-columns-error',
      );
      return false;
    }

    return this.validateAllCsvRules(data);
  }

  async findDefaultLocaleIndex(headers: string[]): Promise<number> {
    let defaultLocale = await this.loadDefaultLocale();

    if (!defaultLocale) {
      return 0;
    }

    let index = headers.findIndex(
      (header) => header?.toString().trim().toLowerCase() === defaultLocale.toLowerCase(),
    );

    // If exact match not found, try user's current locale as fallback
    if (index === -1) {
      let currentLocale = this.session.locale?.split('-')[0];
      if (currentLocale) {
        index = headers.findIndex(
          (header) => header?.toString().trim().toLowerCase() === currentLocale.toLowerCase(),
        );
      }
    }

    // Final fallback to first column
    return index === -1 ? 0 : index;
  }

  async loadDefaultLocale(): Promise<string> {
    try {
      let languageSettings = await this.store.findRecord(
        'messenger-settings/languages',
        this.appService.app.id,
      );
      return languageSettings.defaultLocale;
    } catch (error) {
      return this.session.locale?.split('-')[0] || 'en';
    }
  }

  async validateAllCsvRules(data: string[][]): Promise<boolean> {
    let headers = data[0];
    let defaultLocaleIndex = await this.findDefaultLocaleIndex(headers);
    let dataRows = data.slice(1);

    let unsupportedLanguage = headers.find((languageCode) => !isLocaleSupported(languageCode));

    if (unsupportedLanguage) {
      this.parsingErrorMessage = this.intl.t(
        'new-settings.workspace.multilingual.glossary.import.unsupported-languages-error',
        { languages: unsupportedLanguage },
      );
      return false;
    }

    // Single pass validation through all data rows
    let phrases = new Set<string>();

    let hasError = dataRows.some((row, i) => {
      // Ignore new line at the end of the file
      if (i === dataRows.length - 1 && row.length === 1) {
        return false;
      }

      let isBlankRow = row.every((cell, idx) => {
        if (idx === defaultLocaleIndex) {
          return true; // skip the default locale column
        }
        return !cell || cell === '';
      });

      if (isBlankRow) {
        this.parsingErrorMessage = this.intl.t(
          'new-settings.workspace.multilingual.glossary.import.blank-rows-error',
        );
        return true;
      }

      let defaultPhrase = row[defaultLocaleIndex];

      if (!defaultPhrase) {
        this.parsingErrorMessage = this.intl.t(
          'new-settings.workspace.multilingual.glossary.import.missing-default-phrase-error',
        );
        return true;
      }

      if (phrases.has(defaultPhrase)) {
        this.parsingErrorMessage = this.intl.t(
          'new-settings.workspace.multilingual.glossary.import.duplicate-phrases-error',
        );
        return true;
      }

      phrases.add(defaultPhrase);
      return false;
    });

    return !hasError;
  }

  @action
  async parseAndUploadCsvFile() {
    this.parsingErrorMessage = '';
    this.csvData = [];
    this.fileFailedToUpload = false;
    try {
      if (!this.file) {
        return;
      }

      let results = await this._parseCsvFile(this.file);

      if (!(await this.isValidCsvStructure(results.data))) {
        this.fileFailedToUpload = true;
        this.fileIsValid = false;
        throw new Error(
          this.intl.t('new-settings.workspace.multilingual.glossary.import.parse-error'),
        );
      }

      // Get all rows from the CSV file
      this.csvData = results.data as string[][];
      this.fileIsValid = true;

      this.intercomEventService.trackAnalyticsEvent({
        action: 'uploaded',
        object: 'multilingual_glossary',
        context: 'from_bulk_modal',
      });
    } catch (error) {
      this.fileIsValid = false;
      this.fileFailedToUpload = true;
      if (this.parsingErrorMessage === '') {
        this.parsingErrorMessage = this.intl.t(
          'new-settings.workspace.multilingual.glossary.import.parse-error',
        );
      }
    }
  }

  @dropTask
  *saveGlossary(): TaskGenerator<void> {
    yield taskFor(this.glossaryService.importGlossary).perform(this.csvData, 'from_bulk_modal');
    this.args.onModalClose();
  }

  @dropTask
  *exportGlossary(): TaskGenerator<void> {
    yield this.glossaryService.exportGlossary();
    this.args.onModalClose();
  }

  @action
  setFile(file: File | null) {
    // Reset state completely if a file was previously rejected
    if (this.fileFailedToUpload && file) {
      this.file = null;
      this.fileIsValid = false;
      this.fileFailedToUpload = false;
      this.parsingErrorMessage = '';

      // Now set the new file after resetting state
      setTimeout(() => {
        this.file = file;
        this.parseAndUploadCsvFile();
      }, 0);
      return;
    }

    // Normal flow for first file upload
    this.file = file;
    if (file) {
      this.parseAndUploadCsvFile();
    } else {
      this.fileIsValid = false;
      this.fileFailedToUpload = false;
      this.parsingErrorMessage = '';
    }
  }

  get glossaryExists() {
    return this.glossaryService.exists;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Multilingual::ImportGlossaryModal': typeof ImportGlossaryModal;
  }
}
